/** Transient alpha bounds, relative to the unchanged source rectangle's long edge and centre. */
export interface SymbolInk {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly radius: number;
}

/** Scan once per canonical artwork, before foreground opacity. Include every nonzero-alpha texel. */
export function measureSymbolInk(
  data: Uint8Array,
  width: number,
  height: number,
): SymbolInk | null {
  let left = width,
    top = height,
    right = 0,
    bottom = 0,
    radiusSquared = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!data[(y * width + x) * 4 + 3]) continue;
      left = Math.min(left, x);
      right = Math.max(right, x + 1);
      top = Math.min(top, y);
      bottom = Math.max(bottom, y + 1);
      const dx = Math.abs(x + 0.5 - width / 2) + 0.5;
      const dy = Math.abs(y + 0.5 - height / 2) + 0.5;
      radiusSquared = Math.max(radiusSquared, dx * dx + dy * dy);
    }
  }
  if (left >= right || top >= bottom) return null;
  const longEdge = Math.max(width, height);
  // One canonical texel guards antialiasing differences between display/print preparation.
  return {
    left: (Math.max(0, left - 1) - width / 2) / longEdge,
    top: (Math.max(0, top - 1) - height / 2) / longEdge,
    right: (Math.min(width, right + 1) - width / 2) / longEdge,
    bottom: (Math.min(height, bottom + 1) - height / 2) / longEdge,
    radius: Math.min(Math.hypot(width, height) / 2, Math.sqrt(radiusSquared) + 1) / longEdge,
  };
}
